/*
 * Storefront protection: light deterrence against casual copying.
 *
 * Nothing here can stop a determined visitor. Every image and line of text a
 * browser displays has already been delivered to that browser, and
 * view-source, the browser menus, extensions, screenshots and network tools
 * all remain available. The aim is simply to remove the one-click routes to
 * copying Noralusin copy and saving or dragging out its photography.
 *
 * What it does (outside form fields):
 * - cancels the right-click / long-press context menu;
 * - cancels copy and cut (Ctrl/Cmd+C, Ctrl/Cmd+X and the browser's Edit menu);
 * - cancels text selection starts (the CSS in the snippet also sets
 *   user-select: none on the page);
 * - cancels dragging images, and links that wrap images.
 *
 * What it never touches: inputs, textareas, selects, contenteditable regions
 * and anything inside [data-allow-copy] keep native selection, copy, paste
 * and context menus, so search, quantity fields, the newsletter, contact and
 * comment forms and cart notes work as before. Keyboard navigation (Tab,
 * Enter, Space, arrows, Escape), focus, screen readers, browser find and
 * zoom are unaffected: only the copy / cut shortcuts are cancelled, and no
 * listener calls stopPropagation, so the theme's own click, swipe and key
 * handlers still receive every event.
 *
 * Loaded once from layout/theme.liquid via snippets/storefront-protection.liquid,
 * which skips it entirely inside the Theme Editor (request.design_mode).
 * To switch it off, remove the render line from layout/theme.liquid.
 */
(() => {
  // Belt and braces: the snippet already skips the Theme Editor.
  if (window.Shopify && window.Shopify.designMode) return;

  document.documentElement.classList.add('storefront-protected');

  const MEDIA = 'img, picture, video, canvas';
  const EDITABLE = 'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [data-allow-copy]';

  const elementFrom = (target) => (target instanceof Element ? target : target && target.parentElement);
  const isEditable = (target) => {
    const el = elementFrom(target);
    return Boolean(el && el.closest(EDITABLE));
  };

  document.addEventListener('contextmenu', (event) => {
    if (!isEditable(event.target)) event.preventDefault();
  });

  document.addEventListener('selectstart', (event) => {
    if (!isEditable(event.target)) event.preventDefault();
  });

  // Covers the keyboard shortcuts and the browser's Edit > Copy menu alike.
  // A copy that starts inside a field (e.g. its own typed text) is allowed.
  const blockCopy = (event) => {
    if (!isEditable(document.activeElement) && !isEditable(event.target)) event.preventDefault();
  };
  document.addEventListener('copy', blockCopy);
  document.addEventListener('cut', blockCopy);

  document.addEventListener('keydown', (event) => {
    if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
    if (event.code !== 'KeyC' && event.code !== 'KeyX') return;
    if (isEditable(event.target)) return;
    event.preventDefault();
  });

  // Dragging an image out of the page, on its own or inside a link (a
  // product card link would otherwise carry its image). Pointer/touch events
  // are untouched, so swipe rails and galleries behave as before; the CSS in
  // the snippet adds -webkit-user-drag.
  document.addEventListener('dragstart', (event) => {
    const el = elementFrom(event.target);
    if (!el || isEditable(el)) return;
    const link = el.closest('a');
    if (el.closest(MEDIA) || (link && link.querySelector(MEDIA))) event.preventDefault();
  });
})();
